/**
 * HOW TO USE
 * ----------
 *
 * import { RevealOnScroll } from './RevealOnScroll';
 * new RevealOnScroll('.class-name');
 */

interface RevealStyle {
	opacity?: number | string;
	transform?: string;
	scale?: string;
}

interface RevealConfig {
	rootMargin?: string;
	transitionTime?: string;
	direction?: 'in' | 'both';
	showStyle?: RevealStyle;
	hideStyle?: RevealStyle;
	// eslint-disable-next-line no-unused-vars
	onReveal?: (elements: NodeListOf<HTMLElement>) => void;
}

export class RevealOnScroll {
	private elements: NodeListOf<HTMLElement>;
	private defaultConfig: Required<Omit<RevealConfig, 'onReveal'>> & {
		// eslint-disable-next-line no-unused-vars
		onReveal: (elements: NodeListOf<HTMLElement>) => void;
	};
	private config: RevealConfig;

	constructor(elements: string, config: RevealConfig = {}) {
		this.elements = document.querySelectorAll<HTMLElement>(elements);

		this.defaultConfig = {
			rootMargin: '-80px',
			transitionTime: '.75s',
			direction: 'in',
			showStyle: {
				opacity: 1,
				transform: 'translateY(0)',
			},
			hideStyle: {
				opacity: 0,
				transform: 'translateY(50px)',
			},
			onReveal: () => {
				/* empty */
			},
		};

		this.config = {
			...this.defaultConfig,
			...config,
			showStyle: {
				...this.defaultConfig.showStyle,
				...config.showStyle,
			},
			hideStyle: {
				...this.defaultConfig.hideStyle,
				...config.hideStyle,
			},
		};

		this.reveal();
	}

	private reveal(): void {
		const revealOptions = {
			root: null,
			rootMargin: this.config.rootMargin,
			threshold: 0,
		};

		const revealObserver = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				const target = entry.target as HTMLElement;

				if (entry.isIntersecting) {
					target.classList.add('reveal');
					target.style.opacity = String(this.config.showStyle?.opacity ?? 1);
					target.style.scale = this.config.showStyle?.scale ?? '';
					target.style.transform = this.config.showStyle?.transform ?? '';
					target.style.transition = this.config.transitionTime;
					this.config.onReveal?.(this.elements);
				} else {
					if (this.config.direction === 'both') {
						target.classList.remove('reveal');
					}
					if (!target.classList.contains('reveal')) {
						target.style.opacity = String(this.config.hideStyle?.opacity ?? 0);
						target.style.scale = this.config.hideStyle?.scale ?? '';
						target.style.transform = this.config.hideStyle?.transform ?? '';
						target.style.transition = this.config.transitionTime;
					}
				}
			});
		}, revealOptions);

		this.elements.forEach((element) => revealObserver.observe(element));
	}
}
